import {
  ErrorCode,
  OutletListItem,
  SalesLocationType,
  SystemVisibility,
  CheckoutSummary,
  PaymentMethodListItem,
  CheckoutStatus,
  CheckoutCancelActor,
  CheckoutAdyenPaymentResponse,
  OrderFormat,
  CheckoutMetaDataItemPenguinLockersData,
  CheckoutDetails,
  OutletDetails,
  ServiceTimeKindType,
} from '@ancon/wildcat-types'
import { CheckoutRequestItem } from '@ancon/wildcat-utils/checkout/types'
import isCheckoutItemsAreEqual from '@ancon/wildcat-utils/checkout/isCheckoutItemsAreEqual'
import isUpdatingCheckoutAllowed from '@ancon/wildcat-utils/checkout/isUpdatingCheckoutAllowed'
import isDeletingCheckoutAllowed from '@ancon/wildcat-utils/checkout/isDeletingCheckoutAllowed'
import getT from 'next-translate/getT'
import serializeError from '@ancon/wildcat-utils/error/serializeError'
import isAPIError from '@ancon/wildcat-utils/error/isAPIError'
import APIFilterBuilder from '@ancon/wildcat-utils/api/APIFilterBuilder'
import getCheckoutWalletTopUpMetaData from '@ancon/wildcat-utils/checkout/getCheckoutWalletTopUpMetaData'
import getCheckoutGiftCardMetaData from '@ancon/wildcat-utils/checkout/getCheckoutGiftCardMetaData'
import getCheckoutRequestItemFromCheckoutItem from '@ancon/wildcat-utils/checkout/getCheckoutRequestItemFromCheckoutItem'
import isGiftCardCheckout from '@ancon/wildcat-utils/checkout/isGiftCardCheckout'

import createAppAsyncThunk from '../../../store/createAppAsyncThunk'
import { OutletFilters } from '../../outlet/types'
import {
  outletFiltersSelector,
  outletListItemSelector,
} from '../../outlet/store/outletSelector'
import {
  clientContextCurrentUserCompanyIdSelector,
  clientContextCustomerIdSelector,
  clientContextCustomerNameSelector,
  clientContextCustomerPhoneNumberSelector,
} from '../../clientContext/store/clientContextSelectors'
import api from '../../../api'
import {
  CheckoutErrorContextType,
  CheckoutFilters,
  CheckoutOutOfStockAdditionalData,
  LocalStorageCartMeta,
  CheckoutInvoicePaymentResponse,
  CheckoutInvalidDataErrorAdditionalData,
  UnavailableOverrideMenuItems,
  CapacityConflictRawAdditionalData,
  CheckoutUnavailableOrderTimeErrorAdditionalData,
} from '../types'
import { outletSetOutletFilters } from '../../outlet/store/outletSlice'
import localStorageUtils from '../../app/utils/localStorageUtils'
import getCheckoutErrorModalConfig from '../utils/getCheckoutErrorModalConfig'
import { appShowError } from '../../app/store/appSlice'
import { ErrorModalType } from '../../app/types'
import getLocaleTag from '../../app/utils/getLocalTag'
import appLanguage from '../../app/utils/appLanguage'
import getTenantIds from '../../app/utils/getTenantIds'
import archiveLogger from '../../../utils/archiveLogger'
import getCheckoutMetaData from '../utils/getCheckoutMetaData'
import getCheckoutFiltersFromMetaData from '../utils/getCheckoutFiltersFromMetaData'
import { AOWAllOrderFormats } from '../../app/constants'
import { AsSoonAsPossibleServiceTime } from '../constants'
import {
  AdyenPaymentData,
  AdyenPaymentMethodsResponse,
} from '../../adyen/types'
import getSimpleOutletListItemByOutletDetails from '../utils/getSimpleOutletListItemByOutletDetails'
import isServiceTimeInThePast from '../utils/isServiceTimeInThePast'
import { isPreOrderCheckoutSelector } from '../../preOrder/store/preOrderSelectors'

import {
  checkoutCurrentCheckoutDetailsSelector,
  checkoutCurrentCheckoutIdSelector,
  checkoutCurrentCheckoutMetaDataSelector,
  checkoutCurrentCheckoutOutletEnableCustomOrderNotesSelector,
  checkoutCurrentCheckoutOutletIdSelector,
  checkoutCurrentCheckoutTipAmountSelector,
  checkoutCurrentCheckoutTotalAmountCurrencySelector,
  checkoutFiltersSelector,
  checkoutIdSelector,
  checkoutIsWalletTopUpSelector,
  checkoutItemsSelector,
  checkoutOrderInstructionsSelector,
  checkoutSummaryCheckoutIdSelector,
  checkoutSummaryOutletIdSelector,
  outletSelectedOutletHasCheckoutSelector,
  generalCheckoutCompanyIdSelector,
  checkoutIsGiftCardSelector,
} from './checkoutSelectors'

export const updateCheckoutPenguinLockersMetadata = createAppAsyncThunk<
  void,
  {
    checkoutId: string
    outletId: string
    metadata: CheckoutMetaDataItemPenguinLockersData
  }
>(
  'checkout/updateCheckoutPenguinLockersMetadata',
  async ({ checkoutId, outletId, metadata }) => {
    await api.core.checkouts.put.penguinLockersMetadata(
      { penguinLockersMetadata: metadata },
      { outletId, checkoutId },
    )
  },
)

export const createCheckout = createAppAsyncThunk<
  {
    outletLisItem?: OutletListItem | null
    checkoutId: string
    checkoutFilters: CheckoutFilters
  },
  {
    items: CheckoutRequestItem[]
    outletId: string
    filters?: Partial<OutletFilters>
  }
>(
  'checkout/createCheckout',
  async (
    { items, outletId, filters },
    { getState, rejectWithValue, dispatch },
  ) => {
    const locale = appLanguage.get()
    const outletFilters = outletFiltersSelector(getState())
    const customerId = clientContextCustomerIdSelector(getState())
    const companyId = clientContextCurrentUserCompanyIdSelector(getState())
    const outletLisItem = outletListItemSelector(getState())
    const checkoutFilters = (filters || outletFilters) as CheckoutFilters
    const t = await getT(locale, 'common')

    const outletEnableCustomOrderNotes =
      outletLisItem?.enableCustomOrderNotesInAO

    const orderInstructions = outletEnableCustomOrderNotes
      ? checkoutFilters.delivery?.address?.note?.trim()
      : null

    try {
      const response = await api.core.checkouts.post.create(
        {
          orderFormat: checkoutFilters.orderFormat!,
          serviceTime:
            checkoutFilters.serviceTime || AsSoonAsPossibleServiceTime,
          salesLocation: {
            id: checkoutFilters.salesLocation?.table?.id,
            type: checkoutFilters.salesLocation?.type || SalesLocationType.None,
          },
          delivery: checkoutFilters.delivery,
          customerId,
          companyId,
          items,
          instructions: orderInstructions || null,
        },
        { outletId },
      )

      const checkoutMetadata = getCheckoutMetaData(checkoutFilters)

      const penguinLockersMetaData =
        checkoutMetadata as CheckoutMetaDataItemPenguinLockersData

      if (penguinLockersMetaData?.station) {
        await dispatch(
          updateCheckoutPenguinLockersMetadata({
            checkoutId: response.data.id,
            outletId,
            metadata: penguinLockersMetaData,
          }),
        )
      }

      localStorageUtils.setItem<LocalStorageCartMeta>('cartMeta', {
        outletId,
        checkoutId: response!.data.id,
      })

      return { checkoutId: response!.data.id, outletLisItem, checkoutFilters }
    } catch (err) {
      const errorModalConfig = await getCheckoutErrorModalConfig(
        err,
        {
          title: t('errors.checkoutGenericCreateFailure.title'),
          message: t('errors.checkoutGenericCreateFailure.message'),
        },
        CheckoutErrorContextType.CreateCheckout,
        [ErrorCode.CheckoutPickupTimeCapacityConflict],
        locale,
        { orderFormat: checkoutFilters.orderFormat! },
      )

      if (isAPIError(err, ErrorCode.CheckoutPickupTimeCapacityConflict)) {
        return rejectWithValue({
          capacityConflictErrorPayload: {
            additionalData: err.response?.data
              .additionalData as CapacityConflictRawAdditionalData,
            requestedItems: items,
            requestedTime:
              checkoutFilters.serviceTime || AsSoonAsPossibleServiceTime,
            origin: 'create',
            outletId,
          },
        })
      }

      dispatch(
        appShowError(
          errorModalConfig
            ? {
                errorModalType: ErrorModalType.GeneralCheckoutError,
                ...errorModalConfig,
              }
            : undefined,
        ),
      )
      return rejectWithValue(errorModalConfig)
    }
  },
)

export const fetchCurrentCheckoutDetails = createAppAsyncThunk<
  CheckoutDetails | null,
  { outletId?: string; checkoutId?: string } | undefined
>(
  'checkout/fetchCurrentCheckoutDetails',
  async (data, { getState, rejectWithValue, dispatch }) => {
    const locale = appLanguage.get()
    const t = await getT(locale, 'common')

    try {
      const checkoutOutletId =
        data?.outletId || checkoutCurrentCheckoutOutletIdSelector(getState())!
      const currentCheckoutId =
        data?.checkoutId || checkoutCurrentCheckoutIdSelector(getState())!

      const response = await api.core.checkouts.get.details({
        outletId: checkoutOutletId,
        checkoutId: currentCheckoutId,
      })

      return response?.data ?? null
    } catch (err) {
      const errorModalConfig = await getCheckoutErrorModalConfig(
        err,
        {
          title: t('errors.checkoutGenericFetchDetailsFailure.title'),
          message: t('errors.checkoutGenericFetchDetailsFailure.message'),
          errorModalType: ErrorModalType.CheckoutFetchError,
        },
        undefined,
        undefined,
        locale,
      )
      dispatch(
        appShowError(
          errorModalConfig
            ? {
                errorModalType: ErrorModalType.CheckoutFetchError,
                ...errorModalConfig,
              }
            : undefined,
        ),
      )
      localStorageUtils.removeItem('cartMeta')

      return rejectWithValue(errorModalConfig)
    }
  },
)

export const updateCheckout = createAppAsyncThunk<
  /**
   * Resolves to `{ priceChanged }` so callers (e.g. the pre-payment guard) can
   * tell whether the backend re-priced any item during this update and abort
   * the payment until the customer acknowledges the new price.
   */
  { priceChanged: boolean },
  Partial<{
    items: CheckoutRequestItem[]
    instructions: string
    companyId: string | null
    checkoutErrorContextType: CheckoutErrorContextType
    skipPriceChangeErrorModal: boolean
  }>
>(
  'checkout/updateCheckout',
  async (
    {
      items,
      instructions,
      companyId: externalCompanyId,
      checkoutErrorContextType = CheckoutErrorContextType.UpdateCheckout,
      skipPriceChangeErrorModal = false,
    },
    { getState, dispatch, rejectWithValue },
  ) => {
    const outletId = checkoutCurrentCheckoutOutletIdSelector(getState())!
    const checkoutId = checkoutIdSelector(getState())!
    const customerId = clientContextCustomerIdSelector(getState())
    const checkoutCompanyId = generalCheckoutCompanyIdSelector(getState())
    const checkoutFilters = checkoutFiltersSelector(getState())
    const checkoutItems = checkoutItemsSelector(getState())!
    const checkoutMetaData = checkoutCurrentCheckoutMetaDataSelector(getState())
    const checkoutInstructions = checkoutOrderInstructionsSelector(getState())
    const customerFullName = clientContextCustomerNameSelector(getState())
    const customerPhoneNumber =
      clientContextCustomerPhoneNumberSelector(getState())
    const isWalletTopUpCheckout = checkoutIsWalletTopUpSelector(getState())
    const isGiftCardPurchaseCheckout = checkoutIsGiftCardSelector(getState())
    const locale = appLanguage.get()
    const outletEnableCustomOrderNotes =
      checkoutCurrentCheckoutOutletEnableCustomOrderNotesSelector(getState())

    const t = await getT(locale, 'common')

    const delivery = checkoutFilters.delivery
      ? {
          ...checkoutFilters.delivery,
          recipient: customerFullName,
          phone: customerPhoneNumber!,
        }
      : null

    /**
     * For ASAP checkouts send an empty time instead of the stored resolved
     * timestamp, so the backend re-resolves (and re-prices) at "now".
     */
    const serviceTime =
      checkoutFilters.serviceTime?.kind === ServiceTimeKindType.AsSoonAsPossible
        ? AsSoonAsPossibleServiceTime
        : checkoutFilters.serviceTime!

    if (!outletId || !checkoutId) {
      return { priceChanged: false }
    }

    const walletsMetaData = isWalletTopUpCheckout
      ? getCheckoutWalletTopUpMetaData({
          items: checkoutItems,
          metaData: checkoutMetaData!,
        })
      : undefined

    const giftCardMetaData = isGiftCardPurchaseCheckout
      ? getCheckoutGiftCardMetaData({
          items: checkoutItems,
          metaData: checkoutMetaData!,
        })
      : undefined

    const systemProductMetaData =
      walletsMetaData || giftCardMetaData || undefined

    const attendeeItems = [
      {
        attendeeId: null,
        items:
          items ||
          checkoutItems.map(item =>
            getCheckoutRequestItemFromCheckoutItem(
              item,
              systemProductMetaData,
              true,
            ),
          ),
      },
    ]

    /**
     * The `externalCompanyId` can be null.
     * So, use `externalCompanyId` if it's provided, otherwise send the checkout company id.
     */
    const companyId =
      externalCompanyId === undefined ? checkoutCompanyId : externalCompanyId

    const metadata = getCheckoutMetaData(checkoutFilters, checkoutMetaData)

    const orderInstructions = outletEnableCustomOrderNotes
      ? instructions || checkoutInstructions
      : null

    try {
      const response = await api.core.checkouts.put.update(
        {
          customerId,
          companyId,
          delivery,
          orderFormat: checkoutFilters.orderFormat!,
          serviceTime,
          salesLocation: {
            id: checkoutFilters.salesLocation?.table?.id,
            type: checkoutFilters.salesLocation?.type || SalesLocationType.None,
          },
          attendeeItems,
          instructions: orderInstructions || null,
          metaData: metadata ? JSON.stringify(metadata) : null,
        },
        {
          outletId,
          checkoutId,
        },
      )

      dispatch(fetchCurrentCheckoutDetails({ outletId, checkoutId }))

      const priceChanged = !!response.data.items?.length

      if (!skipPriceChangeErrorModal && priceChanged) {
        const { items: responseItems } = response.data
        // Show price change modal
        const errorModalConfig = await getCheckoutErrorModalConfig(
          {
            response: {
              data: {
                additionalData: responseItems,
                errorCode: ErrorCode.CheckoutPriceChanged,
              },
            },
          },
          {
            title: t('errors.checkoutGenericPriceChanged.title'),
            message: t('errors.checkoutGenericPriceChanged.message'),
          },
          undefined,
          undefined,
          locale,
        )
        dispatch(
          appShowError(
            errorModalConfig
              ? {
                  errorModalType: ErrorModalType.CheckoutItemPriceChanged,
                  ...errorModalConfig,
                }
              : undefined,
          ),
        )
      }

      return { priceChanged }
    } catch (err: any) {
      if (isAPIError(err, ErrorCode.CheckoutIsFrozen)) {
        return { priceChanged: false }
      }

      // Update outlet filters to the checkout filters
      const outletSelectedOutletHasCheckout =
        outletSelectedOutletHasCheckoutSelector(getState())
      const checkoutDetails = checkoutCurrentCheckoutDetailsSelector(getState())

      if (checkoutDetails && outletSelectedOutletHasCheckout) {
        const metaDataFilters = getCheckoutFiltersFromMetaData(checkoutDetails)

        dispatch(
          outletSetOutletFilters({
            orderFormat: checkoutDetails.orderFormat,
            // A passed scheduled time is rejected by every update, so keep the current one
            ...(!isServiceTimeInThePast(checkoutDetails.serviceTime) && {
              serviceTime: checkoutDetails.serviceTime,
            }),
            delivery: checkoutDetails.delivery || undefined,
            salesLocation: checkoutDetails.salesLocation,
            section: metaDataFilters?.section,
          }),
        )
      }

      const errorModalConfig = await getCheckoutErrorModalConfig(
        err,
        {
          title: t('errors.checkoutGenericUpdateFailure.title'),
          message: t('errors.checkoutGenericUpdateFailure.message'),
        },
        checkoutErrorContextType,
        [ErrorCode.CheckoutPickupTimeCapacityConflict],
        locale,
        { orderFormat: checkoutFilters.orderFormat! },
      )

      if (isAPIError(err, ErrorCode.CheckoutPickupTimeCapacityConflict)) {
        return rejectWithValue({
          capacityConflictErrorPayload: {
            additionalData: err.response!.data!
              .additionalData as CapacityConflictRawAdditionalData,
            requestedItems: attendeeItems[0].items,
            // Captured here because the rejected update rolls the stored filters
            // back to the server's time, losing the one that was refused.
            requestedTime: serviceTime,
            origin: 'update',
            outletId,
          },
        })
      }

      // The scheduled time has passed, let the customer pick a new one
      if (isAPIError(err, ErrorCode.ServiceTimeInThePast)) {
        const additionalData: CheckoutUnavailableOrderTimeErrorAdditionalData =
          {
            isPreOrderCheckout: isPreOrderCheckoutSelector(getState()),
          }

        dispatch(
          appShowError(
            errorModalConfig
              ? {
                  errorModalType: ErrorModalType.CheckoutUnavailableOrderTime,
                  ...errorModalConfig,
                  additionalData,
                }
              : undefined,
          ),
        )

        return rejectWithValue({
          ...errorModalConfig,
          isServiceTimeInThePast: true,
        })
      }

      const isStockError =
        err?.response?.data?.errorCode === ErrorCode.PaymentAcceptOutOfStock

      if (isStockError) {
        const additionalData: CheckoutOutOfStockAdditionalData[] =
          err.response?.data?.additionalData || []

        if (
          checkoutErrorContextType !== CheckoutErrorContextType.UpdateCheckout
        ) {
          dispatch(
            appShowError(
              errorModalConfig
                ? {
                    errorModalType: ErrorModalType.GeneralCheckoutError,
                    ...errorModalConfig,
                    additionalData,
                  }
                : undefined,
            ),
          )
        }

        return rejectWithValue({
          ...errorModalConfig,
          stockErrorAdditionalData: additionalData,
        })
      }

      const isInvalidItemDueToOverrideMenu =
        err?.response?.data?.errorCode ===
        ErrorCode.CheckoutItemUnavailableDueToOverrideMenu

      if (isInvalidItemDueToOverrideMenu) {
        const additionalData: UnavailableOverrideMenuItems = {
          checkoutItemProductVariantIds:
            err.response?.data?.additionalData || [],
        }
        dispatch(
          appShowError(
            errorModalConfig
              ? {
                  errorModalType:
                    ErrorModalType.CheckoutUnavailableProductsDueToOverrideMenu,
                  ...errorModalConfig,
                  additionalData,
                }
              : undefined,
          ),
        )
        return rejectWithValue({
          ...errorModalConfig,
          invalidDataErrorAdditionalData: additionalData,
        })
      }

      if (
        isAPIError(err, ErrorCode.RequestDataNotValid) &&
        checkoutFilters.orderFormat === OrderFormat.BoxPickup &&
        checkoutErrorContextType === CheckoutErrorContextType.UpdateCheckout
      ) {
        const additionalData = err.response?.data
          ?.additionalData as CheckoutInvalidDataErrorAdditionalData[]

        dispatch(
          appShowError(
            errorModalConfig
              ? {
                  errorModalType:
                    ErrorModalType.CheckoutBoxPickupUnAvailableProducts,
                  ...errorModalConfig,
                  additionalData,
                }
              : undefined,
          ),
        )

        return rejectWithValue({
          ...errorModalConfig,
          invalidDataErrorAdditionalData: additionalData,
        })
      }

      dispatch(
        appShowError(
          errorModalConfig
            ? {
                errorModalType: ErrorModalType.GeneralCheckoutError,
                ...errorModalConfig,
              }
            : undefined,
        ),
      )

      return rejectWithValue(errorModalConfig)
    }
  },
)

/** Returns created checkout id or undefined if the checkout already exists */
export const addCheckoutItem = createAppAsyncThunk<
  | {
      checkoutId: string
    }
  | undefined,
  {
    items: CheckoutRequestItem[]
    outletId?: string
    filters?: Partial<OutletFilters>
  }
>(
  'checkout/addCheckoutItem',
  async (
    { items, outletId: fallbackOutletId, filters },
    { getState, dispatch },
  ) => {
    const checkoutOutletId = checkoutCurrentCheckoutOutletIdSelector(getState())
    const checkoutId = checkoutIdSelector(getState())
    const checkoutItems = checkoutItemsSelector(getState())

    const outletId = checkoutOutletId || fallbackOutletId

    let createdCheckoutId: string | undefined

    if (!checkoutId) {
      const createdCheckout = await dispatch(
        createCheckout({ items, outletId: outletId!, filters }),
      ).unwrap()
      createdCheckoutId = createdCheckout.checkoutId
    } else {
      const modifiedCheckoutItems = checkoutItems.reduce<CheckoutRequestItem[]>(
        (acc, checkoutItem) => {
          const checkoutRequestItem = getCheckoutRequestItemFromCheckoutItem(
            checkoutItem,
            undefined,
            true,
          )

          items.every((newItem, index) => {
            if (isCheckoutItemsAreEqual(checkoutRequestItem, newItem)) {
              checkoutRequestItem.quantity += newItem.quantity
              items.splice(index, 1)
              return false
            }
            return true
          })

          acc.push(checkoutRequestItem)

          return acc
        },
        [],
      )

      await dispatch(
        updateCheckout({
          items: [...modifiedCheckoutItems, ...items],
          checkoutErrorContextType: CheckoutErrorContextType.AddCheckoutItem,
        }),
      ).unwrap()
    }

    await dispatch(fetchCurrentCheckoutDetails())

    return createdCheckoutId
      ? {
          checkoutId: createdCheckoutId,
        }
      : undefined
  },
)

export const updateCheckoutItem = createAppAsyncThunk<
  void,
  {
    item: CheckoutRequestItem
    checkoutItemId: string
    outletId?: string
    filters?: Partial<OutletFilters>
  }
>(
  'checkout/updateCheckoutItem',
  async (
    { item, checkoutItemId, outletId: fallbackOutletId, filters },
    { getState, dispatch },
  ) => {
    const checkoutOutletId = checkoutCurrentCheckoutOutletIdSelector(getState())
    const checkoutId = checkoutIdSelector(getState())
    const checkoutItems = checkoutItemsSelector(getState())

    const outletId = checkoutOutletId || fallbackOutletId

    if (!checkoutId) {
      await dispatch(
        createCheckout({ items: [item], outletId: outletId!, filters }),
      ).unwrap()
    } else {
      const modifiedCheckoutItems = checkoutItems.reduce<CheckoutRequestItem[]>(
        (acc, checkoutItem) => {
          if (checkoutItem.id === checkoutItemId) {
            acc.push(item)
            return acc
          }

          const checkoutRequestItem = getCheckoutRequestItemFromCheckoutItem(
            checkoutItem,
            undefined,
            true,
          )

          acc.push(checkoutRequestItem)

          return acc
        },
        [],
      )

      await dispatch(
        updateCheckout({
          items: modifiedCheckoutItems,
          checkoutErrorContextType: CheckoutErrorContextType.UpdateCheckout,
        }),
      ).unwrap()
    }

    await dispatch(fetchCurrentCheckoutDetails())
  },
)

export const deleteCheckout = createAppAsyncThunk<
  void,
  | {
      skipClearState?: boolean
    }
  | undefined
>('checkout/deleteCheckout', async (_, { getState, dispatch }) => {
  const locale = appLanguage.get()
  const outletId = checkoutCurrentCheckoutOutletIdSelector(getState())!
  const checkoutId = checkoutIdSelector(getState())!
  const checkoutDetails = checkoutCurrentCheckoutDetailsSelector(getState())

  const t = await getT(locale, 'common')

  try {
    // Skip making request if payment started, it would fail
    if (!checkoutDetails || isDeletingCheckoutAllowed(checkoutDetails)) {
      await api.core.checkouts.delete.deleteCheckout(undefined, {
        outletId,
        checkoutId,
      })
    }

    localStorageUtils.removeItem('cartMeta')
  } catch (err) {
    if (isAPIError(err, ErrorCode.DeleteCheckoutWhenPaymentStarted)) {
      // Ignore payment started error
      localStorageUtils.removeItem('cartMeta')
      return
    }

    const errorModalConfig = await getCheckoutErrorModalConfig(
      err,
      {
        title: t('errors.checkoutGenericDeleteFailure.title'),
        message: t('errors.checkoutGenericDeleteFailure.message'),
      },
      undefined,
      undefined,
      locale,
    )
    dispatch(
      appShowError(
        errorModalConfig
          ? {
              errorModalType: ErrorModalType.GeneralCheckoutError,
              ...errorModalConfig,
            }
          : undefined,
      ),
    )
  }
})

export const generalCheckoutApplyDiscount = createAppAsyncThunk<
  void,
  { code: string }
>(
  'checkout/generalCheckoutApplyDiscount',
  async ({ code }, { getState, dispatch, rejectWithValue }) => {
    const outletId = checkoutCurrentCheckoutOutletIdSelector(getState())!
    const checkoutId = checkoutCurrentCheckoutIdSelector(getState())!
    const locale = appLanguage.get()

    const t = await getT(locale, 'common')

    try {
      await api.core.checkouts.put.applyDiscount(
        { code },
        { outletId, checkoutId },
      )
      dispatch(fetchCurrentCheckoutDetails())

      return undefined
    } catch (err) {
      const discountError = await getCheckoutErrorModalConfig(
        err,
        {
          title: t('errors.discountGenericError.title'),
          message: t('errors.discountGenericError.message'),
        },
        undefined,
        undefined,
        locale,
      )

      return rejectWithValue(discountError)
    }
  },
)

export const changeCheckoutItemQuantity = createAppAsyncThunk<
  void,
  { checkoutItemId: string; quantity: number }
>(
  'checkout/changeCheckoutItemQuantity',
  async ({ checkoutItemId, quantity }, { getState, dispatch }) => {
    const checkoutItems = checkoutItemsSelector(getState())!

    const modifiedCheckoutRequestItems = checkoutItems.map(item => {
      const checkoutRequestItem = getCheckoutRequestItemFromCheckoutItem(
        item,
        undefined,
        true,
      )

      if (item.id === checkoutItemId) {
        checkoutRequestItem.quantity = quantity
      }

      return checkoutRequestItem
    })

    await dispatch(
      updateCheckout({
        items: modifiedCheckoutRequestItems,
        checkoutErrorContextType:
          CheckoutErrorContextType.ChangeCheckoutItemQuantity,
      }),
    ).unwrap()

    dispatch(fetchCurrentCheckoutDetails())
  },
)

const fetchCheckoutOutletDetails = createAppAsyncThunk<
  OutletDetails | null,
  {
    outletId: string
  }
>('checkout/fetchCheckoutOutletDetails', async ({ outletId }) => {
  const response = await api.core.outlet.get.details({
    outletId,
  })

  return response.data ?? null
})

export const fetchCheckoutOutletListItem = createAppAsyncThunk<
  OutletListItem | null,
  {
    outletId?: string
    /** If no nearby outlet found, create fallback data from outlet details */
    useOutletDetailsFallback?: boolean
  }
>(
  'checkout/fetchCheckoutOutletListItem',
  async ({ outletId, useOutletDetailsFallback }, { dispatch }) => {
    try {
      const tenantIds = getTenantIds()

      const apiFilterBuilder = new APIFilterBuilder()

      if (tenantIds?.length) {
        apiFilterBuilder.addProperty('tenant.id').in(tenantIds)
      }

      const response = await api.core.outlets.get.nearby({
        outletId,
        orderFormats: AOWAllOrderFormats,
        systemVisibility: SystemVisibility.OrderWeb,
        filter: apiFilterBuilder.build(),
      })

      let outletListItem = response.data?.items[0]

      if (!outletListItem && outletId && useOutletDetailsFallback) {
        const outlet = await dispatch(
          fetchCheckoutOutletDetails({ outletId }),
        ).unwrap()

        if (outlet) {
          outletListItem = getSimpleOutletListItemByOutletDetails(outlet)
        }
      }

      return outletListItem ?? null
    } catch (error) {
      return null
    }
  },
)

export const validateCheckout = createAppAsyncThunk<
  { checkout: CheckoutDetails; outlet: OutletListItem } | undefined,
  { checkoutId?: string; outletId?: string }
>(
  'checkout/validateCheckout',
  async (args, { getState, dispatch, rejectWithValue }) => {
    function getCheckoutIdAndOutletId() {
      const internalCheckoutId = checkoutCurrentCheckoutIdSelector(getState())
      const internalOutletId =
        checkoutCurrentCheckoutOutletIdSelector(getState())

      if (internalCheckoutId && internalOutletId) {
        return {
          currentCheckoutId: internalCheckoutId,
          checkoutOutletId: internalOutletId,
        }
      }

      const checkout =
        localStorageUtils.getItem<LocalStorageCartMeta>('cartMeta')
      if (checkout?.checkoutId && checkout?.outletId) {
        return {
          currentCheckoutId: checkout.checkoutId,
          checkoutOutletId: checkout.outletId,
        }
      }

      // If checkoutId and outletId are provided use them
      if (args?.checkoutId && args?.outletId) {
        return {
          currentCheckoutId: args.checkoutId,
          checkoutOutletId: args.outletId,
        }
      }

      return {
        currentCheckoutId: undefined,
        checkoutOutletId: undefined,
      }
    }

    // Skip checkout validation if checkoutId and outletId are not exist
    const { currentCheckoutId, checkoutOutletId } = getCheckoutIdAndOutletId()
    if (!currentCheckoutId && !checkoutOutletId) {
      return rejectWithValue({})
    }

    try {
      const checkoutDetails = await dispatch(
        fetchCurrentCheckoutDetails({
          outletId: checkoutOutletId,
          checkoutId: currentCheckoutId,
        }),
      ).unwrap()

      if (!checkoutDetails) {
        // TODO:  handle error - checkout not found
        localStorageUtils.removeItem('cartMeta')
        return rejectWithValue({})
      }

      const outletListItem = await dispatch(
        fetchCheckoutOutletListItem({
          outletId: checkoutOutletId ?? undefined,
          /**
           * For gift-card purchase checkouts, fetch detailed outlet info to create outlet list item fallback
           * because the nearby endpoint won't return outlet if the AOW is in maintenance mode.
           */
          useOutletDetailsFallback: isGiftCardCheckout(checkoutDetails),
        }),
      ).unwrap()
      if (!outletListItem || outletListItem.id !== checkoutOutletId) {
        // TODO:  handle error - checkout outlet not found
        rejectWithValue({})
      }

      if (isUpdatingCheckoutAllowed(checkoutDetails)) {
        await dispatch(updateCheckout({})).unwrap()
      }

      return { checkout: checkoutDetails, outlet: outletListItem! }
    } catch (err) {
      // TODO:  handle error
      return rejectWithValue(err)
    }
  },
)

export const deleteCheckoutItems = createAppAsyncThunk<
  { isCheckoutDeleted?: boolean },
  { checkoutItemIds: string[]; companyId?: string | null }
>(
  'checkout/deleteCheckoutItems',
  async ({ checkoutItemIds, companyId }, { getState, dispatch }) => {
    const checkoutItems = checkoutItemsSelector(getState())!

    const modifiedCheckoutRequestItems = checkoutItems.reduce((acc, item) => {
      if (!checkoutItemIds.includes(item.id)) {
        const checkoutRequestItem = getCheckoutRequestItemFromCheckoutItem(
          item,
          undefined,
          true,
        )
        acc.push(checkoutRequestItem)
      }
      return acc
    }, [] as CheckoutRequestItem[])

    if (modifiedCheckoutRequestItems.length === 0) {
      await dispatch(deleteCheckout()).unwrap()
      return { isCheckoutDeleted: true }
    }

    await dispatch(
      updateCheckout({
        items: modifiedCheckoutRequestItems,
        companyId,
        checkoutErrorContextType:
          CheckoutErrorContextType.ChangeCheckoutItemQuantity,
      }),
    ).unwrap()

    return {}
  },
)

export const fetchCheckoutSummary = createAppAsyncThunk<
  {
    checkoutSummary: CheckoutSummary
    outlet: OutletListItem
    timestamp: string
  },
  { outletId: string; checkoutId: string; isBackgroundFetch?: boolean }
>(
  'checkout/fetchCheckoutSummary',
  async ({ checkoutId, outletId }, { dispatch, rejectWithValue }) => {
    const locale = appLanguage.get()

    const t = await getT(locale, 'common')

    try {
      const checkoutResponse = await api.core.checkouts.get.summary({
        outletId,
        checkoutId,
      })

      const checkoutSummary = checkoutResponse.data

      const nearbyOutletsResponse = await api.core.outlets.get.nearby({
        systemVisibility: SystemVisibility.OrderApp,
        orderFormats: AOWAllOrderFormats,
        limit: 1,
        offset: 0,
        outletId,
      })

      let outletListItem = nearbyOutletsResponse.data.items[0]

      if (!outletListItem && isGiftCardCheckout(checkoutSummary)) {
        const outlet = await dispatch(
          fetchCheckoutOutletDetails({ outletId }),
        ).unwrap()

        if (outlet) {
          outletListItem = getSimpleOutletListItemByOutletDetails(outlet)
        }
      }

      return {
        checkoutSummary,
        outlet: outletListItem,
        timestamp: checkoutResponse.headers.date,
      }
    } catch (err) {
      const errorModalConfig = await getCheckoutErrorModalConfig(
        err,
        {
          title: t('errors.checkoutGenericFetchDetailsFailure.title'),
          message: t('errors.checkoutGenericFetchDetailsFailure.message'),
          errorModalType: ErrorModalType.CheckoutFetchError,
        },
        undefined,
        undefined,
        locale,
      )

      dispatch(
        appShowError(
          errorModalConfig
            ? {
                errorModalType: ErrorModalType.CheckoutFetchError,
                ...errorModalConfig,
              }
            : undefined,
        ),
      )

      return rejectWithValue(errorModalConfig)
    }
  },
)

export const fetchCheckoutAdyenPaymentMethods =
  createAppAsyncThunk<AdyenPaymentMethodsResponse>(
    'checkout/fetchCheckoutAdyenPaymentMethods',
    async (_, { getState, rejectWithValue }) => {
      const language = appLanguage.get()

      const outletId = checkoutCurrentCheckoutOutletIdSelector(getState())!
      const checkoutId = checkoutCurrentCheckoutIdSelector(getState())!

      try {
        const response = await api.core.payments.post.paymentMethods(
          {
            channel: 'web',
            shopperLocale: getLocaleTag(language).replace('-', '_'),
          },
          {
            outletId,
            checkoutId,
          },
        )

        return response.data
      } catch (err: any) {
        return rejectWithValue(serializeError(err))
      }
    },
  )

export const createCheckoutAdyenPayment = createAppAsyncThunk<
  CheckoutAdyenPaymentResponse,
  {
    data: AdyenPaymentData
    correlationId: string
  }
>(
  'checkout/createCheckoutAdyenPayment',
  async ({ data, correlationId }, { getState, rejectWithValue }) => {
    const outletId = checkoutCurrentCheckoutOutletIdSelector(getState())!
    const companyId = clientContextCurrentUserCompanyIdSelector(getState())
    const checkoutId = checkoutCurrentCheckoutIdSelector(getState())!
    const tipAmount = checkoutCurrentCheckoutTipAmountSelector(getState())
    const currency =
      checkoutCurrentCheckoutTotalAmountCurrencySelector(getState())

    // Sent to archive service
    const debugInfo = {
      CheckoutId: checkoutId,
      OutletId: outletId,
      CorrelationId: correlationId,
      Method: 'Adyen Checkout',
    }

    archiveLogger.debug('Payment started', debugInfo)

    try {
      const response = await api.core.payments.post.createAdyenPayment(
        {
          paymentData: data,
          ...(companyId && { companyId }),
          ...(tipAmount && {
            tipAmount: {
              amount: tipAmount,
              currency,
            },
          }),
        },
        {
          outletId,
          checkoutId,
        },
        {
          'x-channel': 'web',
          'x-correlation-id': correlationId,
        },
      )
      return response!.data
    } catch (err: any) {
      const isStockError =
        err?.response?.data?.errorCode === ErrorCode.PaymentAcceptOutOfStock

      if (isStockError) {
        const additionalData: CheckoutOutOfStockAdditionalData[] =
          err.response?.data?.additionalData || []

        return rejectWithValue({
          ...serializeError(err),
          stockErrorAdditionalData: additionalData,
        })
      }

      return rejectWithValue(serializeError(err))
    }
  },
)

export const createInvoicePayment = createAppAsyncThunk<
  CheckoutInvoicePaymentResponse,
  {
    invoiceReferenceNote: string
    correlationId: string
  }
>(
  'checkout/createInvoicePayment',
  async (
    { invoiceReferenceNote, correlationId },
    { getState, rejectWithValue },
  ) => {
    const outletId = checkoutCurrentCheckoutOutletIdSelector(getState())!
    const companyId = clientContextCurrentUserCompanyIdSelector(getState())!
    const checkoutId = checkoutCurrentCheckoutIdSelector(getState())!

    // Sent to archive service
    const debugInfo = {
      CheckoutId: checkoutId,
      OutletId: outletId,
      CorrelationId: correlationId,
      Method: 'Invoice Checkout',
    }

    archiveLogger.debug('Payment started', debugInfo)

    try {
      const response = await api.core.payments.post.createInvoicePayment(
        {
          companyId,
          invoiceReferenceNote,
        },
        {
          outletId,
          checkoutId,
        },
        {
          'x-correlation-id': correlationId,
        },
      )

      return response!.data
    } catch (err: any) {
      const isStockError =
        err?.response?.data?.errorCode === ErrorCode.PaymentAcceptOutOfStock

      if (isStockError) {
        const additionalData: CheckoutOutOfStockAdditionalData[] =
          err.response?.data?.additionalData || []

        return rejectWithValue({
          ...serializeError(err),
          stockErrorAdditionalData: additionalData,
        })
      }

      return rejectWithValue(serializeError(err))
    }
  },
)

export const submitCheckoutAdyenDetails = createAppAsyncThunk<
  CheckoutAdyenPaymentResponse | undefined,
  {
    data: AdyenPaymentData
    correlationId: string
    outletId: string
    checkoutId: string
  }
>(
  'checkout/submitCheckoutAdyenDetails',
  async (
    { data, correlationId, outletId, checkoutId },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.core.payments.post.details(
        data,
        {
          outletId,
          checkoutId,
        },
        {
          'x-channel': 'web',
          'x-correlation-id': correlationId,
        },
      )

      // Bug: Core can respond with 423 and no data
      return response?.data
    } catch (err: any) {
      return rejectWithValue(serializeError(err))
    }
  },
)

export const cancelGeneralCheckout = createAppAsyncThunk<void>(
  'checkout/cancelGeneralCheckout',
  async (_, { getState, dispatch, rejectWithValue }) => {
    const checkoutId = checkoutSummaryCheckoutIdSelector(getState())!
    const outletId = checkoutSummaryOutletIdSelector(getState())!
    const cancelActorId = clientContextCustomerIdSelector(getState())!
    const locale = appLanguage.get()

    const t = await getT(locale, 'common')

    try {
      await api.core.checkouts.put.cancel(
        { cancelActor: CheckoutCancelActor.Customer, cancelActorId },
        { outletId, checkoutId },
      )
      return undefined
    } catch (err) {
      const errorModalConfig = await getCheckoutErrorModalConfig(
        err,
        {
          title: t('errors.checkoutGenericCancelFailure.title'),
          message: t('errors.checkoutGenericCancelFailure.message'),
        },
        undefined,
        undefined,
        locale,
      )
      dispatch(
        appShowError(
          errorModalConfig
            ? {
                errorModalType: ErrorModalType.GeneralCheckoutError,
                ...errorModalConfig,
              }
            : undefined,
        ),
      )
      return rejectWithValue(errorModalConfig)
    }
  },
)

export const fetchCheckoutWayOfPayments = createAppAsyncThunk<
  PaymentMethodListItem[]
>('checkout/fetchCheckoutWayOfPayments', async (_, { getState }) => {
  const outletId = checkoutCurrentCheckoutOutletIdSelector(getState())!

  const response = await api.core.checkouts.get.wayOfPayments({
    outletId,
  })

  return response!.data.wayOfPayments || []
})

export const fetchCheckoutStatus = createAppAsyncThunk<
  { status: CheckoutStatus; timestamp: string },
  {
    outletId: string
    checkoutId: string
  }
>('checkout/fetchCheckoutStatus', async ({ outletId, checkoutId }) => {
  const response = await api.core.checkouts.get.status({
    outletId,
    checkoutId,
  })

  const timestamp = response.headers.date

  return {
    status: response.data.status,
    timestamp,
  }
})
